import React from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  FileText,
  Search,
  ShoppingBag,
  Building2,
  Award,
  Zap,
  DollarSign,
  HeadphonesIcon,
  CheckCircle,
  ArrowRight,
  Check,
} from "lucide-react";
import ContactForm from "../components/ContactForm";
import ServiceSlider from "../components/ServiceSlider";
import WhyChooseUsSlider from "../components/WhyChooseUsSlider";
import HowItWorksSlider from "../components/HowItWorksSlider";
import PopupButton from "../components/PopupButton";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  services,
  pricingPlans,
  faqs,
  howItWorks,
  whyChooseUs,
} from "../data/mock";
import SEO from "../components/SEO";

const iconMap = {
  Shield,
  FileText,
  Search,
  ShoppingBag,
  Building2,
  Award,
  Zap,
  DollarSign,
  HeadphonesIcon,
  CheckCircle,
};

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Trademark Claim Experts | Protect Your Brand with Trademark Registration"
        description="Trademark Claim Experts offers trusted trademark registration, copyright registration, trademark search, Amazon Brand Registry, and company formation services for businesses across the USA."
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-brand-purple/5 via-white to-brand-pink/5 py-20 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-pink/20 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
        <div
          className="absolute bottom-0 left-0 w-96 h-96 bg-brand-purple/20 rounded-full filter blur-3xl opacity-20 animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  File Your
                  <span className="block bg-gradient-to-r from-brand-purple to-brand-pink bg-clip-text text-transparent">
                    Trademark
                  </span>
                </h1>
                <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                  <span className="font-bold text-gray-900">
                    Safeguard Your Brand. Prevent Infringement.
                  </span>{" "}
                  Secure complete legal protection for your business name, logo,
                  and brand identity.
                  <span className="block mt-3 font-bold bg-gradient-to-r from-brand-pink to-brand-purple bg-clip-text text-transparent text-xl md:text-2xl">
                    Starting at just $45 • 24-Hour Filing • Trusted Service
                  </span>
                </p>
              </div>
              <div className="flex flex-wrap gap-4 mt-8">
                <PopupButton
                  size="lg"
                  className="bg-gradient-to-r from-brand-purple to-brand-pink hover:opacity-90 text-white px-8 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                >
                  Register My Trademark
                  <ArrowRight className="ml-2 w-5 h-5" />
                </PopupButton>
                <Button
                  size="lg"
                  variant="outline"
                  className="js-chat border-2 border-brand-purple text-brand-purple hover:bg-brand-pink/5 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Live Chat
                </Button>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-6 items-center pt-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-gray-600">
                    No Hidden Charges{" "}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-gray-600">
                    24/7 Customer Support{" "}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-gray-600">
                    Money-Back Promise
                  </span>
                </div>
              </div>
            </div>

            {/* Enhanced Form with Glow Effect */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-brand-purple to-brand-pink rounded-3xl blur-2xl opacity-20 animate-pulse"></div>
              <div className="relative bg-white p-8 md:p-10 rounded-3xl shadow-2xl border border-blue-100">
                <div className="mb-6 text-center">
                  <h3 className="text-3xl font-bold bg-gradient-to-r from-brand-purple to-brand-pink bg-clip-text text-transparent mb-3">
                    Claim Your Brand Rights
                  </h3>
                  <p className="text-gray-600 text-lg">
                    <span className="font-bold bg-gradient-to-r from-brand-pink to-brand-purple bg-clip-text text-transparent">
                      Only $45!
                    </span>{" "}
                    Secure your brand with our quick and dependable trademark
                    registration services.
                  </p>
                </div>
                <ContactForm variant="compact" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section 1 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Trusted Trademark Registration for Businesses
              </h2>
              <p className="text-lg text-gray-600">
                Trademark Claim Experts makes protecting your brand simple,
                affordable, and stress-free. Whether you're launching a new
                company or expanding an established business, our experienced
                team manages every step of the registration process—from
                application preparation to USPTO filing—with accuracy and
                efficiency.
              </p>
              <p className="text-lg text-gray-600">
                Our commitment to transparent pricing, personalized assistance,
                and reliable service makes us a trusted choice for businesses
                across the United States. We focus on delivering a smooth
                registration experience while helping you secure long-term legal
                protection for your brand.
              </p>

              <PopupButton
                size="lg"
                className="bg-gradient-to-r from-brand-purple to-brand-pink hover:opacity-90 text-white px-8 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                Protect My Trademark
              </PopupButton>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl flex items-center justify-center bg-white p-8">
                <img
                  src="/assets/img/protect-my-trademark.jpg"
                  alt="Protect My Trademark"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">
            Why Businesses Choose Trademark Claim Experts
          </h2>
          <WhyChooseUsSlider items={whyChooseUs.slice(0, 3)} />

          <div className="grid lg:grid-cols-2 gap-12 items-center py-20">
            <div className="relative">
              <div className="w-full h-[500px] bg-gradient-to-br from-brand-pink/10 to-brand-purple/10 rounded-2xl flex items-center justify-center p-4">
                <img
                  src="/assets/img/starting-a-business-proyect.svg"
                  alt="Business Growth and Trust Illustration"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            <div className="space-y-6 pt-8">
              <h3 className="text-3xl font-bold text-gray-900">
                Discover More Reasons Why Business Trust Us
              </h3>
              <p className="text-lg text-gray-600">
                Building trust requires dedication, reliability, and exceptional
                customer service. Businesses nationwide depend on us because we
                combine legal knowledge with personalized support, ensuring
                every trademark application receives the attention it deserves.
              </p>
              <ul className="space-y-4">
                {whyChooseUs.slice(3).map((item) => {
                  const Icon = iconMap[item.icon];
                  return (
                    <li key={item.id} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-brand-pink/5 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-brand-purple" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {item.title}
                        </h4>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Trademark & Brand Protection Services
            </h2>
            <p className="text-lg text-gray-600">
              Helping businesses secure their intellectual property with
              confidence.
            </p>
          </div>
          <ServiceSlider services={services} />
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-brand-purple/5 to-brand-pink/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Trademark Registration Process
            </h2>
            <p className="text-lg text-gray-600">
              Fast, simple, and hassle-free from start to finish
            </p>
          </div>
          <HowItWorksSlider items={howItWorks} />
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-brand-purple/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Affordable Trademark Protection Plans
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative flex flex-col h-full hover:shadow-2xl transition-all duration-300 ${plan.recommended ? "ring-2 ring-brand-purple scale-105" : ""}`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-brand-purple to-brand-pink text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Recommended
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-6">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gray-900">
                      ${plan.price}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    +Federal Filing Fee
                  </p>
                  <CardDescription className="mt-4">
                    {plan.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow overflow-y-auto max-h-64 pb-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="mt-auto pt-4">
                  <PopupButton className="w-full bg-gradient-to-r from-brand-purple to-brand-pink hover:opacity-90 text-white">
                    Register Your Trademark
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </PopupButton>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-purple to-brand-pink text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Protect Your Brand — Starting at Only $45
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            Register your trademark today with experienced professionals and
            confidence every step of the way.
          </p>
          <div className="flex justify-center gap-4">
            <PopupButton
              size="lg"
              variant="secondary"
              className="bg-white text-brand-purple hover:bg-gray-100"
            >
              Protect My Trademark
            </PopupButton>
            <Button
              size="lg"
              variant="outline"
              className="js-chat border-white text-white hover:bg-white hover:text-brand-purple"
            >
              Live Chat
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Find answers to the most common questions businesses ask before
            registering a trademark.
          </p>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="text-left font-semibold">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

    </div>
  );
};

export default Home;
